import { Link } from 'react-router-dom';
import {
  NotebookPen,
  PhoneCall,
  CalendarClock,
  ReceiptText,
  Truck,
  Package,
  Repeat,
  Calendar,
  MapPin,
} from 'lucide-react';
import Reveal from '../components/ui/Reveal';
import { industries } from '../data/industries';
import { modules, apps } from '../data/modules';
import nvidiaLogo from '../assets/images/nvidia.jpeg';
import azureLogo from '../assets/images/microsoft-azure.jpeg';
import metaLogo from '../assets/images/meta tecn partner.png';
import './Home.css';

const heroStats = [
  { value: '7', label: 'Core modules, one platform' },
  { value: '2', label: 'Companion apps — field & customer' },
  { value: '5', label: 'Service industries served' },
];

const painPoints = [
  {
    icon: NotebookPen,
    title: 'Service history lives in notebooks, not systems',
    detail: 'A technician’s memory is the only record of what was serviced, when, and with what parts — until they forget or leave.',
  },
  {
    icon: PhoneCall,
    title: 'The phone is the bottleneck',
    detail: 'Every booking, reschedule, and status check ties up a person on a call, so growth means hiring more office staff, not more technicians.',
  },
  {
    icon: CalendarClock,
    title: 'Recurring contracts get missed',
    detail: 'AMC renewals, refill rounds, and scheduled maintenance visits slip through the cracks without an automatic trigger.',
  },
  {
    icon: ReceiptText,
    title: 'Billing happens after the fact, by hand',
    detail: 'Parts, labor, and completed jobs get tallied manually at month-end, delaying invoices and inviting errors.',
  },
  {
    icon: Truck,
    title: 'No visibility into the field',
    detail: 'The office has no real-time view of where a technician is, what job they’re on, or whether it’s actually done.',
  },
  {
    icon: Package,
    title: 'Parts run out at the worst time',
    detail: 'Without stock tracking, a van shows up to a job without the part it needs — costing a second visit.',
  },
];

export default function Home() {
  return (
    <div className="home">
      <section className="hero">
        <div className="container hero-inner">
          <Reveal as="div" className="hero-copy">
            <span className="eyebrow">Field Service &amp; AMC Operations Platform</span>
            <h1>
              One platform to run <span className="mark">every service job</span>,
              from the first call to the closed invoice.
            </h1>
            <p>
              Tecnvirons brings appointments, workforce management, customers, orders,
              and recurring service contracts into a single system — built for RO servicing,
              vending machine operators, AC &amp; fridge repair, medical equipment maintenance,
              and civil construction businesses.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
              <Link to="/platform" className="btn btn-secondary">Explore the Platform</Link>
            </div>
            <div className="hero-stats">
              {heroStats.map((stat) => (
                <div key={stat.label} className="hero-stat">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal as="div" className="hero-visual">
            <div className="hero-partners-badge card">
              <div className="hero-partners-row">
                <span className="hero-partners-label">Incepted by</span>
                <div className="hero-partners-logos">
                  <div className="hero-partner-item">
                    <img src={nvidiaLogo} alt="NVIDIA" className="hero-partner-logo" />
                    <span className="hero-partner-name">NVIDIA</span>
                  </div>
                  <div className="hero-partner-item">
                    <img src={azureLogo} alt="Microsoft Azure" className="hero-partner-logo" />
                    <span className="hero-partner-name">Microsoft Azure Cloud</span>
                  </div>
                </div>
              </div>
              <div className="hero-partners-divider" />
              <div className="hero-partners-row">
                <span className="hero-partners-label">Recognized as a</span>
                <div className="hero-partners-logos">
                  <div className="hero-partner-item">
                    <img src={metaLogo} alt="Meta Tech Partner" className="hero-partner-logo hero-partner-logo-wide" />
                    <span className="hero-partner-name">Meta Tech Partner</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="hero-card card">
              <div className="hero-card-row">
                <span className="pill"><Repeat size={14} /> Subscription Order</span>
                <span className="hero-card-status">Active</span>
              </div>
              <div className="hero-card-title">RO Filter Change — Quarterly AMC</div>
              <div className="hero-card-meta">Next visit auto-scheduled · Technician assigned</div>
              <div className="hero-card-progress">
                <div className="hero-card-progress-bar" />
              </div>
              <div className="hero-card-footer">
                <span><MapPin size={14} /> Assigned to Ramesh K.</span>
                <span>Bill auto-generated</span>
              </div>
            </div>
            <div className="hero-card card hero-card-float">
              <div className="hero-card-row">
                <span className="pill"><Calendar size={14} /> Appointment</span>
                <span className="hero-card-status">Confirmed</span>
              </div>
              <div className="hero-card-title">AC Repair — Booked via WhatsApp</div>
              <div className="hero-card-meta">Reminder sent · Slot confirmed</div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">The Problem</span>
            <h2>Service businesses run on memory, not systems.</h2>
            <p>
              RO servicing, vending, AC/fridge repair, medical equipment maintenance, and
              civil construction all share the same operational pain — work that happens
              in the field, tracked (or lost) back at the office.
            </p>
          </Reveal>

          <Reveal as="div" stagger className="pain-grid">
            {painPoints.map((point) => (
              <div key={point.title} className="pain-card card reveal-item">
                <div className="pain-icon"><point.icon size={26} strokeWidth={1.6} /></div>
                <h3>{point.title}</h3>
                <p>{point.detail}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">Who It's Built For</span>
            <h2>Five industries. One operational backbone.</h2>
            <p>Different trades, the same underlying job: schedule it, dispatch it, prove it, bill it.</p>
          </Reveal>

          <Reveal as="div" stagger className="industry-grid">
            {industries.map((ind) => (
              <Link key={ind.id} to="/industries" className="industry-card card reveal-item">
                <div className="industry-icon"><ind.icon size={28} strokeWidth={1.6} /></div>
                <h3>{ind.name}</h3>
                <p>{ind.tagline}</p>
                <span className="industry-link">See how we help →</span>
              </Link>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">The Platform</span>
            <h2>Seven modules that work as one system.</h2>
            <p>No more stitching together a calendar tool, a spreadsheet, and a chat group.</p>
          </Reveal>

          <Reveal as="div" stagger className="module-grid">
            {modules.map((mod) => (
              <div key={mod.id} className="module-card card reveal-item">
                <div className="module-icon"><mod.icon size={24} strokeWidth={1.6} /></div>
                <h3>{mod.name}</h3>
                <p>{mod.summary}</p>
              </div>
            ))}
          </Reveal>

          <Reveal className="module-cta">
            <Link to="/platform" className="btn btn-primary">See every module in detail</Link>
          </Reveal>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">Two Apps, One Platform</span>
            <h2>Your field team and your customers, both covered.</h2>
            <p>The office dashboard is only half the picture — Tecnvirons reaches your crews and your customers directly.</p>
          </Reveal>

          <div className="apps-grid">
            {apps.map((app, i) => (
              <Reveal key={app.id} className={`app-card card ${i === 1 ? 'app-card-alt' : ''}`}>
                <span className="pill">{app.audience}</span>
                <h3>{app.name}</h3>
                <p className="app-tagline">{app.tagline}</p>
                <p>{app.description}</p>
                <ul className="app-points">
                  {app.points.map((pt) => (
                    <li key={pt}>{pt}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <Reveal className="cta-box card">
            <h2>See your business running on one system.</h2>
            <p>Book a walkthrough and we’ll map your exact workflow — RO servicing, vending, repair, or construction — onto the platform live.</p>
            <div className="hero-actions center">
              <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
              <Link to="/about" className="btn btn-secondary">Learn about us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
